import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Round P136 (Feb 2026) — shared hook that fetches the curated set of
 * structurally-leveraged CEFs (PDI, PDO, UTG, DSL, etc.) ONCE per
 * session and caches it in a module-level Set. Consumers can then
 * do O(1) `set.has(ticker)` checks without re-fetching.
 *
 * Backend source: GET /api/leveraged-cefs → { tickers: [...] }.
 * Curated list lives in `backend/data/leveraged_cefs.py`.
 *
 * Consumers:
 *   • <StockTable> — inline ⚡ chip next to the CEF badge
 *   • <PortfolioView> — same
 *   • <StockModal> — ⚡ chip next to the CEF badge in the modal header
 *   • Screener preset "Leveraged CEFs" filter
 *   • /glossary/cef-strategies — already uses the enriched
 *     /cef-strategies endpoint directly, doesn't need this hook
 */

let cachedSet = null;
let inflightPromise = null;

async function loadOnce() {
  if (cachedSet) return cachedSet;
  if (inflightPromise) return inflightPromise;
  inflightPromise = axios
    .get(`${API}/leveraged-cefs`)
    .then((res) => {
      const arr = (res.data && res.data.tickers) || [];
      cachedSet = new Set(arr.map((t) => (t || '').toUpperCase()));
      return cachedSet;
    })
    .catch(() => {
      // Fail open — no highlight rather than a broken page.
      cachedSet = new Set();
      return cachedSet;
    })
    .finally(() => {
      inflightPromise = null;
    });
  return inflightPromise;
}

export function useLeveragedCefs() {
  const [set, setSet] = useState(cachedSet);
  useEffect(() => {
    let cancelled = false;
    if (!cachedSet) {
      loadOnce().then((s) => {
        if (!cancelled) setSet(s);
      });
    }
    return () => {
      cancelled = true;
    };
  }, []);
  return set || new Set();
}

/** Direct predicate for non-React callers (e.g. filter callbacks). */
export function isLeveragedCef(ticker) {
  if (!ticker || !cachedSet) return false;
  return cachedSet.has(ticker.toUpperCase());
}
